
import React, { useState, useEffect } from 'react';
import { Company, User, UserRole, CurrencyConfig } from '../types';
import { api } from '../services/apiService';
import { REVENUE_COLOR_PALETTE } from '../constants';
import AddUserModal from '../components/AddUserModal';
import UserManagementModal from '../components/UserManagementModal';

interface SettingsViewProps {
  user: User;
  company: Company;
  onCompanyUpdate: (updatedCompany: Company) => void;
  onUserUpdate?: (updatedUser: User) => void;
}

const SettingsView: React.FC<SettingsViewProps> = ({ user, company, onCompanyUpdate, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'team' | 'preferences' | 'messagerie'>('profile');
  const [users, setUsers] = useState<User[]>([]);
  const [availableCurrencies, setAvailableCurrencies] = useState<CurrencyConfig[]>([]);
  const [activitySectors, setActivitySectors] = useState<string[]>([]);
  const [newSectorName, setNewSectorName] = useState('');
  const [isSavingSector, setIsSavingSector] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const DEFAULT_BRACKETS = ['<5K', '5-20K', '20-50K', '50-100K', '>100K'];
  const [revenueBrackets, setRevenueBrackets] = useState<string[]>(company.revenueBrackets ?? DEFAULT_BRACKETS);
  const [revenueColors, setRevenueColors] = useState<Record<string, string>>(company.revenueColors ?? {});
  const [newBracket, setNewBracket] = useState('');
  const [isSavingBrackets, setIsSavingBrackets] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [accountMessage, setAccountMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [smtpForm, setSmtpForm] = useState({ smtp_host: '', smtp_port: 587, smtp_enc: 'tls', smtp_user: '', smtp_pass: '', from_name: user.name || '', from_email: user.email || '' });
  const [smtpHasPassword, setSmtpHasPassword] = useState(false);
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  const [smtpMessage, setSmtpMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [companyForm, setCompanyForm] = useState({
    name: company.name,
    address: company.address || '',
    rcs: company.rcs || '',
    siret: company.siret || '',
    phone: company.phone || '',
    email: company.email || '',
    website: company.website || ''
  });
  const [accountForm, setAccountForm] = useState({
    email: user.email || '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    api.getSmtpSettings().then(data => {
      if (data) {
        setSmtpForm(prev => ({ ...prev, smtp_host: data.smtp_host, smtp_port: data.smtp_port, smtp_enc: data.smtp_enc, smtp_user: data.smtp_user, from_name: data.from_name || prev.from_name, from_email: data.from_email || prev.from_email }));
        setSmtpHasPassword(data.has_password);
      }
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const [companyUsers, config] = await Promise.all([
          api.getCompanyUsers(company.id),
          api.getSystemConfig()
      ]);
      if (
        user.role === UserRole.COMPANY_ADMIN &&
        !companyUsers.some(u => String(u.id) === String(user.id))
      ) {
        setUsers([user, ...companyUsers]);
      } else {
        setUsers(companyUsers);
      }
      setAvailableCurrencies(config.currencies);
      if (config.activitySectors && config.activitySectors.length > 0) {
        setActivitySectors(config.activitySectors);
      } else {
        const sectors = await api.getActivitySectors();
        setActivitySectors(sectors || []);
      }
    };
    fetchData();
  }, [company.id]);

  useEffect(() => {
    setAccountForm(prev => ({ ...prev, email: user.email || '' }));
  }, [user.email]);

  const handleSaveSmtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSmtpMessage(null);
    setIsSavingSmtp(true);
    try {
      const payload: any = { smtp_host: smtpForm.smtp_host, smtp_port: smtpForm.smtp_port, smtp_enc: smtpForm.smtp_enc, smtp_user: smtpForm.smtp_user, from_name: smtpForm.from_name, from_email: smtpForm.from_email };
      if (smtpForm.smtp_pass) payload.smtp_pass = smtpForm.smtp_pass;
      await api.saveSmtpSettings(payload);
      setSmtpHasPassword(true);
      setSmtpForm(prev => ({ ...prev, smtp_pass: '' }));
      setSmtpMessage({ type: 'success', text: 'Configuration SMTP enregistrée.' });
    } catch (err: any) {
      setSmtpMessage({ type: 'error', text: err.message || 'Erreur lors de la sauvegarde.' });
    } finally {
      setIsSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    setSmtpMessage(null);
    setIsTestingSmtp(true);
    try {
      const result = await api.testSmtp();
      setSmtpMessage(result.success ? { type: 'success', text: result.message || 'Connexion SMTP réussie !' } : { type: 'error', text: result.error || 'Échec du test SMTP.' });
    } catch {
      setSmtpMessage({ type: 'error', text: 'Erreur réseau lors du test.' });
    } finally {
      setIsTestingSmtp(false);
    }
  };

  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const updated = await api.updateCompany(company.id, companyForm);
      onCompanyUpdate(updated);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCurrencyChange = async (currencyCode: string) => {
    // Mise à jour immédiate pour l'UX
    const updated = await api.updateCompany(company.id, { currency: currencyCode });
    onCompanyUpdate(updated);
  };

  const handleAddUser = async (userData: Omit<User, 'id'>) => {
    const newUser = await api.createUser(userData);
    setUsers([...users, newUser]);
    setShowAddUserModal(false);
  };

  const handleUserUpdate = async () => {
    // Recharger la liste des utilisateurs apres une modification
    const companyUsers = await api.getCompanyUsers(company.id);
    if (
      user.role === UserRole.COMPANY_ADMIN &&
      !companyUsers.some(u => String(u.id) === String(user.id))
    ) {
      setUsers([user, ...companyUsers]);
    } else {
      setUsers(companyUsers);
    }
  };

  const handleSaveMyAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountMessage(null);
    const nextEmail = accountForm.email.trim();
    if (!nextEmail) {
      setAccountMessage({ type: 'error', text: "L'email est obligatoire." });
      return;
    }
    if (accountForm.password && accountForm.password !== accountForm.confirmPassword) {
      setAccountMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
      return;
    }

    setIsSavingAccount(true);
    try {
      const updated = await api.updateUser(user.id, {
        email: nextEmail,
        ...(accountForm.password ? { password: accountForm.password } : {})
      });
      onUserUpdate?.(updated);
      setAccountForm(prev => ({ ...prev, password: '', confirmPassword: '' }));
      setAccountMessage({ type: 'success', text: 'Compte mis a jour.' });
      await handleUserUpdate();
    } catch {
      setAccountMessage({ type: 'error', text: 'Impossible de mettre a jour le compte.' });
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleAddSector = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSectorName.trim() || isSavingSector) return;
    setIsSavingSector(true);
    try {
      const updated = await api.addActivitySector(newSectorName.trim());
      setActivitySectors(updated);
      setNewSectorName('');
    } finally {
      setIsSavingSector(false);
    }
  };

  const handleAddBracket = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newBracket.trim();
    if (!trimmed || revenueBrackets.includes(trimmed)) return;
    setRevenueBrackets([...revenueBrackets, trimmed]);
    setNewBracket('');
  };

  const handleRemoveBracket = (bracket: string) => {
    setRevenueBrackets(revenueBrackets.filter(b => b !== bracket));
    setRevenueColors(prev => { const n = { ...prev }; delete n[bracket]; return n; });
  };

  const handleSelectColor = (bracket: string, hex: string) => {
    setRevenueColors(prev => ({
      ...prev,
      [bracket]: prev[bracket] === hex ? '' : hex  // toggle si on clique la même couleur
    }));
  };

  const handleSaveBrackets = async () => {
    setIsSavingBrackets(true);
    try {
      // Nettoyer les entrées vides de revenueColors
      const cleanedColors: Record<string, string> = {};
      Object.entries(revenueColors).forEach(([k, v]) => { if (v) cleanedColors[k] = v; });
      const updated = await api.updateCompany(company.id, { revenueBrackets, revenueColors: cleanedColors });
      onCompanyUpdate(updated);
    } finally {
      setIsSavingBrackets(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto animate-in fade-in duration-500">
      {/* Barre d'onglets premium */}
      <div className="flex rounded-[1.5rem] border p-1.5 mb-8 shadow-sm overflow-x-auto orion-settings-tabs-shell">
        {[
          { id: 'profile', label: 'Profil Entreprise' },
          { id: 'team', label: 'Gestion Équipe' },
          { id: 'preferences', label: 'Préférences' },
          { id: 'messagerie', label: 'Messagerie' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-8 py-3.5 rounded-2xl text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === tab.id 
                ? 'orion-settings-tab-active text-white shadow-lg'
                : 'orion-settings-tab text-slate-500'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conteneur principal */}
      <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-xl shadow-slate-200/40 overflow-hidden min-h-[500px]">
        {activeTab === 'profile' && (
          <div className="p-12 animate-in fade-in orion-settings-profile">
            <div className="mb-10">
              <h3 className="text-2xl font-extrabold text-slate-900 mb-2 tracking-tight">Identité Entreprise</h3>
              <p className="text-slate-500 text-sm font-medium">Gérez vos informations contractuelles et coordonnées officielles.</p>
            </div>
            
            <form onSubmit={handleSaveCompany} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {Object.entries(companyForm).map(([key, value]) => (
                <div key={key} className="space-y-2">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">{key}</label>
                  <input 
                    type="text" 
                    value={value} 
                    onChange={e => setCompanyForm({...companyForm, [key]: e.target.value})}
                    className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium transition-all outline-none orion-settings-input"
                  />
                </div>
              ))}
              <div className="md:col-span-2 pt-8 flex justify-end">
                <button type="submit" className="text-white font-bold px-12 py-4 rounded-2xl transition-all shadow-xl orion-settings-primary-btn">
                  {isSaving ? 'Synchronisation...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>

            {user.role === UserRole.COMPANY_ADMIN && (
              <div className="mt-12 border-t border-slate-200 pt-10">
                <div className="mb-6">
                  <h4 className="text-xl font-extrabold text-slate-900 mb-1">Mon compte administrateur</h4>
                  <p className="text-sm text-slate-500">Modifiez votre email de connexion et votre mot de passe.</p>
                </div>
                <form onSubmit={handleSaveMyAccount} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2 md:col-span-3">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Email de connexion</label>
                    <input
                      type="email"
                      value={accountForm.email}
                      onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium transition-all outline-none orion-settings-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Nouveau mot de passe</label>
                    <input
                      type="password"
                      value={accountForm.password}
                      onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                      placeholder="Laisser vide si inchangé"
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium transition-all outline-none orion-settings-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Confirmation mot de passe</label>
                    <input
                      type="password"
                      value={accountForm.confirmPassword}
                      onChange={(e) => setAccountForm({ ...accountForm, confirmPassword: e.target.value })}
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium transition-all outline-none orion-settings-input"
                    />
                  </div>
                  <div className="md:col-span-3 flex items-center justify-between pt-3">
                    <div className={`text-xs font-bold ${accountMessage?.type === 'error' ? 'text-rose-500' : 'text-emerald-600'}`}>
                      {accountMessage?.text || ''}
                    </div>
                    <button type="submit" className="text-white font-bold px-8 py-3 rounded-2xl transition-all shadow-xl orion-settings-primary-btn" disabled={isSavingAccount}>
                      {isSavingAccount ? 'Mise a jour...' : 'Mettre a jour mon compte'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="p-12 animate-in fade-in orion-settings-preferences">
            <h3 className="text-2xl font-extrabold text-slate-900 mb-10 tracking-tight">Réglages Système</h3>
            <div className="grid grid-cols-1 gap-8">
              {/* Gestion des utilisateurs (Admin uniquement) */}
              {user.role === UserRole.COMPANY_ADMIN && (
                <div className="p-10 rounded-[2rem] border orion-settings-pref-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-black text-slate-900 mb-2 flex items-center gap-2">
                        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        Gestion des utilisateurs
                      </h4>
                      <p className="text-sm text-slate-600 font-medium">Créer, modifier ou supprimer les utilisateurs de votre entreprise</p>
                      <p className="text-xs text-slate-500 mt-1">{users.length} utilisateur{users.length > 1 ? 's' : ''} • {company.userLimit} maximum autorisé{company.userLimit > 1 ? 's' : ''}</p>
                    </div>
                    <button
                      onClick={() => setShowUserManagement(true)}
                      className="text-white font-bold px-8 py-4 rounded-2xl transition-all shadow-xl whitespace-nowrap orion-settings-primary-btn"
                    >
                      👥 Gérer les utilisateurs
                    </button>
                  </div>
                </div>
              )}

              {/* Devise */}
              <div className="p-10 rounded-[2rem] border orion-settings-pref-card">
                <h4 className="font-bold text-slate-900 mb-6">Devise de transaction</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {availableCurrencies.map(c => (
                    <button 
                      key={c.code} 
                      onClick={() => handleCurrencyChange(c.code)}
                      className={`py-4 px-4 rounded-xl text-left border transition-all flex flex-col ${company.currency === c.code ? 'orion-settings-currency-active text-white shadow-lg scale-105' : 'orion-settings-currency-idle text-slate-500'}`}
                    >
                      <span className="text-lg font-black mb-1">{c.symbol} {c.code}</span>
                      <span className="text-[10px] uppercase opacity-70">{c.name}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-[10px] text-slate-400 font-medium">Ceci mettra à jour l'affichage des montants dans tout le CRM immédiatement.</p>
              </div>

              <div className="p-10 rounded-[2rem] border orion-settings-pref-card">
                <h4 className="font-bold text-slate-900 mb-3">Secteurs d'activite</h4>
                <p className="text-xs text-slate-500 mb-4">Ces secteurs seront proposes dans la fiche prospect.</p>
                <form onSubmit={handleAddSector} className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newSectorName}
                    onChange={(e) => setNewSectorName(e.target.value)}
                    placeholder="Ajouter un secteur"
                    className="flex-1 border rounded-xl px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="submit"
                    disabled={isSavingSector || !newSectorName.trim()}
                    className="orion-settings-primary-btn text-white font-bold px-5 py-3 rounded-xl disabled:opacity-50"
                  >
                    {isSavingSector ? 'Ajout...' : 'Ajouter'}
                  </button>
                </form>
                {activitySectors.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucun secteur configure.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {activitySectors.map(sector => (
                      <span key={sector} className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700">
                        {sector}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Paliers de revenu potentiel + couleurs — Admin uniquement */}
              {user.role === UserRole.COMPANY_ADMIN && (
                <div className="p-10 rounded-[2rem] border orion-settings-pref-card">
                  <h4 className="font-bold text-slate-900 mb-1">Paliers de revenu potentiel &amp; couleurs</h4>
                  <p className="text-xs text-slate-500 mb-6">
                    Définissez jusqu'à 5 tranches et associez une couleur à chacune pour identifier visuellement
                    les leads à fort potentiel dans toutes les vues.
                  </p>

                  {/* Liste des paliers avec color swatches */}
                  <div className="mb-6 rounded-2xl border border-slate-100 dark:border-white/10 overflow-hidden">
                    {revenueBrackets.length === 0 ? (
                      <p className="text-xs text-slate-400 italic px-4 py-3">Aucun palier configuré.</p>
                    ) : (
                      revenueBrackets.map((bracket, idx) => {
                        const selectedColor = revenueColors[bracket];
                        return (
                          <div
                            key={bracket}
                            className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 dark:border-white/[0.07] last:border-0 transition-colors"
                          >
                            {/* Numéro de palier */}
                            <span className="text-[10px] font-black text-slate-400 dark:text-white/30 w-4 text-center flex-shrink-0 tabular-nums">
                              {idx + 1}
                            </span>

                            {/* Nom du palier avec badge coloré */}
                            <div
                              className={`min-w-[76px] px-3 py-1.5 rounded-xl text-xs font-black text-center border-2 transition-all ${
                                !selectedColor
                                  ? 'bg-slate-100 dark:bg-white/10 border-slate-200 dark:border-white/15 text-slate-500 dark:text-white/60'
                                  : ''
                              }`}
                              style={selectedColor ? {
                                backgroundColor: selectedColor + '25',
                                borderColor: selectedColor + '80',
                                color: selectedColor
                              } : undefined}
                            >
                              {bracket}
                            </div>

                            {/* Palette de couleurs */}
                            <div className="flex items-center gap-2 flex-1">
                              {REVENUE_COLOR_PALETTE.map(c => (
                                <button
                                  key={c.hex}
                                  type="button"
                                  title={c.label}
                                  onClick={() => handleSelectColor(bracket, c.hex)}
                                  className="w-5 h-5 rounded-full flex-shrink-0 transition-all duration-150 focus:outline-none"
                                  style={{
                                    backgroundColor: c.hex,
                                    transform: selectedColor === c.hex ? 'scale(1.4)' : 'scale(1)',
                                    boxShadow: selectedColor === c.hex
                                      ? `0 0 0 2px rgba(255,255,255,0.15), 0 0 0 3.5px ${c.hex}`
                                      : 'none',
                                    opacity: selectedColor && selectedColor !== c.hex ? 0.35 : 1
                                  }}
                                />
                              ))}
                              {selectedColor && (
                                <button
                                  type="button"
                                  title="Effacer la couleur"
                                  onClick={() => handleSelectColor(bracket, selectedColor)}
                                  className="ml-0.5 text-[11px] font-bold text-slate-400 hover:text-rose-400 transition-colors leading-none"
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            {/* Supprimer le palier */}
                            <button
                              type="button"
                              onClick={() => handleRemoveBracket(bracket)}
                              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 dark:text-white/25 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors text-sm"
                              title={`Supprimer ${bracket}`}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Ajouter un palier */}
                  <form onSubmit={handleAddBracket} className="flex gap-2 mb-6">
                    <input
                      type="text"
                      value={newBracket}
                      onChange={(e) => setNewBracket(e.target.value)}
                      placeholder="ex: 100-500K ou >1M"
                      className="flex-1 border rounded-xl px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="submit"
                      disabled={!newBracket.trim() || revenueBrackets.includes(newBracket.trim())}
                      className="orion-settings-primary-btn text-white font-bold px-5 py-3 rounded-xl disabled:opacity-50"
                    >
                      + Ajouter
                    </button>
                  </form>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <p className="text-[10px] text-slate-400">
                      Les couleurs s'affichent sur les fiches prospects dans toutes les vues.
                    </p>
                    <button
                      type="button"
                      onClick={handleSaveBrackets}
                      disabled={isSavingBrackets}
                      className="orion-settings-primary-btn text-white font-bold px-6 py-2.5 rounded-xl text-sm disabled:opacity-50"
                    >
                      {isSavingBrackets ? 'Sauvegarde...' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'messagerie' && (
          <div className="p-12 animate-in fade-in">
            <div className="mb-10">
              <h3 className="text-2xl font-extrabold text-slate-900 mb-2 tracking-tight">Configuration Messagerie</h3>
              <p className="text-slate-500 text-sm font-medium">Configurez votre serveur SMTP pour envoyer des emails depuis vos fiches prospects.</p>
            </div>

            <div className="max-w-2xl">
              <div className="p-8 rounded-[2rem] border orion-settings-pref-card mb-6">
                <p className="text-xs leading-relaxed orion-settings-info-text">
                  <strong className="orion-settings-info-title">Exemples de configuration :</strong><br/>
                  Gmail : smtp.gmail.com · Port 587 · TLS · (mot de passe d'application requis)<br/>
                  OVH : ssl0.ovh.net · Port 465 · SSL<br/>
                  Brevo : smtp-relay.brevo.com · Port 587 · TLS
                </p>
              </div>

              <form onSubmit={handleSaveSmtp} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Serveur SMTP</label>
                    <input type="text" value={smtpForm.smtp_host} onChange={e => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com" required
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium outline-none orion-settings-input" />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Port</label>
                    <input type="number" value={smtpForm.smtp_port} onChange={e => setSmtpForm({ ...smtpForm, smtp_port: Number(e.target.value) })}
                      placeholder="587" required min={1} max={65535}
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium outline-none orion-settings-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Chiffrement</label>
                  <div className="flex gap-3">
                    {(['tls', 'ssl', 'none'] as const).map(enc => (
                      <button key={enc} type="button" onClick={() => setSmtpForm({ ...smtpForm, smtp_enc: enc })}
                        className={`px-5 py-3 rounded-xl text-sm font-bold border transition-all ${smtpForm.smtp_enc === enc ? 'orion-settings-tab-active text-white shadow' : 'orion-settings-tab text-slate-500'}`}>
                        {enc === 'tls' ? 'STARTTLS (587)' : enc === 'ssl' ? 'SSL/TLS (465)' : 'Aucun (25)'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Identifiant SMTP (email)</label>
                    <input type="email" value={smtpForm.smtp_user} onChange={e => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })}
                      placeholder="vous@gmail.com" required
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium outline-none orion-settings-input" />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                      Mot de passe SMTP {smtpHasPassword && <span className="text-emerald-500 ml-1">✓ configuré</span>}
                    </label>
                    <input type="password" value={smtpForm.smtp_pass} onChange={e => setSmtpForm({ ...smtpForm, smtp_pass: e.target.value })}
                      placeholder={smtpHasPassword ? 'Laisser vide pour conserver' : 'Mot de passe SMTP'}
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium outline-none orion-settings-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Nom affiché (expéditeur)</label>
                    <input type="text" value={smtpForm.from_name} onChange={e => setSmtpForm({ ...smtpForm, from_name: e.target.value })}
                      placeholder="Laurent Attar"
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium outline-none orion-settings-input" />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Email expéditeur (reply-to)</label>
                    <input type="email" value={smtpForm.from_email} onChange={e => setSmtpForm({ ...smtpForm, from_email: e.target.value })}
                      placeholder="vous@gmail.com"
                      className="w-full border rounded-2xl px-5 py-4 text-slate-900 font-medium outline-none orion-settings-input" />
                  </div>
                </div>

                {smtpMessage && (
                  <div className={`px-5 py-3 rounded-xl text-sm font-bold ${smtpMessage.type === 'success' ? 'text-emerald-600' : 'text-rose-500'}`}>
                    {smtpMessage.text}
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <button type="button" onClick={handleTestSmtp} disabled={isTestingSmtp || !smtpHasPassword}
                    className="px-6 py-3 rounded-xl text-sm font-bold border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition-all disabled:opacity-40">
                    {isTestingSmtp ? 'Test en cours...' : 'Tester la connexion'}
                  </button>
                  <button type="submit" disabled={isSavingSmtp}
                    className="orion-settings-primary-btn text-white font-bold px-10 py-4 rounded-2xl shadow-xl disabled:opacity-50">
                    {isSavingSmtp ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'team' && (
          <div className="p-12 animate-in fade-in orion-settings-team">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
              <div>
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2 tracking-tight">Équipe & Accès</h3>
                <p className="text-slate-500 text-sm font-medium">Contrôlez les droits de vos collaborateurs.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddUserModal(true)}
                className="orion-settings-invite-btn font-bold px-8 py-3.5 rounded-2xl transition-all border shadow-sm"
              >
                + Inviter un collaborateur
              </button>
            </div>
            
            <div className="overflow-x-auto rounded-[2rem] border border-slate-100 shadow-sm orion-settings-table-shell">
              <table className="w-full text-left">
                <thead className="text-slate-400 text-[10px] uppercase font-bold tracking-widest orion-settings-table-head">
                  <tr>
                    <th className="px-10 py-6">Collaborateur</th>
                    <th className="px-10 py-6">Rôle</th>
                    <th className="px-10 py-6 text-right">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 bg-white orion-settings-table-body">
                  {users.map(u => (
                    <tr key={u.id} className="text-slate-700 transition-colors group orion-settings-table-row">
                      <td className="px-10 py-7">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-sm orion-settings-avatar">
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{u.name}</div>
                            <div className="text-xs text-slate-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-10 py-7">
                        <span className={`px-3 py-1 rounded-lg text-[9px] font-bold uppercase orion-settings-role-pill ${u.role === UserRole.COMPANY_ADMIN ? 'orion-settings-role-admin' : 'orion-settings-role-user'}`}>
                          {u.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-10 py-7 text-right">
                        <span className="text-xs font-bold flex items-center justify-end gap-1.5 orion-settings-status-active">
                          <span className="w-1.5 h-1.5 rounded-full animate-pulse orion-settings-status-dot"></span>
                          Actif
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Message pour utiliser la nouvelle gestion */}
            {user.role === UserRole.COMPANY_ADMIN && (
              <div className="mt-6 p-6 rounded-2xl border orion-settings-info">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 flex-shrink-0 orion-settings-info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-bold orion-settings-info-title">💡 Nouvelle fonctionnalité !</p>
                    <p className="text-xs mt-1 orion-settings-info-text">
                      Utilisez la section <strong>Préférences → Gestion des utilisateurs</strong> pour modifier ou supprimer des utilisateurs.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal ajout utilisateur (ancienne méthode) */}
      {showAddUserModal && (
        <AddUserModal 
            companyId={company.id} 
            onClose={() => setShowAddUserModal(false)} 
            onSubmit={handleAddUser} 
        />
      )}

      {/* Modal de gestion complète des utilisateurs (nouvelle méthode) */}
      {showUserManagement && (
        <UserManagementModal
          companyId={company.id}
          currentUser={user}
          onClose={() => setShowUserManagement(false)}
          onUpdate={handleUserUpdate}
        />
      )}
    </div>
  );
};

export default SettingsView;

